import React from 'react';
import { Button, notification } from 'antd';
import { AddOutlined, EditOutlined } from '@mui/icons-material';
import './OverviewCardInfo.scss';
import { CustomModal } from '@/components/CustomModal';
import classNames from 'classnames';
import { UseMutationResult } from '@tanstack/react-query';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { closeModal, openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { TUTORIALS } from 'src/enums/ETutorials';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import LearnMore from '@/components/Buttons/LearnMore';

export const OverViewCardInfo: React.FC<{
	info: React.ReactNode;
	form: React.ReactNode;
	footer?: React.ReactNode;
	formTitle?: React.ReactNode;
	formActionType?: 'Add' | 'Edit';
	displayFormActionBtn?: boolean;
	displayTutorialActionBtn?: boolean;
	customClasses?: string;
	width?: number | string;
	modalId: MODAL_IDS;
	tutorialId?: TUTORIALS;
	mutation?: UseMutationResult<unknown, Error, any, unknown>;
	onEditClick?: () => void;
	debrand?: boolean;
	icon?: React.ReactNode;
}> = ({
	info,
	form,
	formTitle,
	customClasses,
	formActionType,
	width,
	footer,
	modalId,
	tutorialId,
	onEditClick,
	displayFormActionBtn = true,
	displayTutorialActionBtn = false,
	debrand,
	icon,
}) => {
	const dispatch = useAppDispatch();

	const loggedInUser = useAppSelector(getLoggedInUser);
	const canAddScenario = useAppSelector(selectCanAddScenario);

	const handleClick = () => {
		if (formActionType === 'Add' && !canAddScenario) {
			notification.error({
				message: 'Scenario limit reached',
				description: 'Max scenarios (100) reached. Please Delete existing to add more.',
			});
			return;
		}

		if (onEditClick) {
			onEditClick();
		} else {
			dispatch(openModal({ modalId }));
		}
	};

	const handleConfirm = () => {
		dispatch(closeModal());
	};

	return (
		<div
			className={classNames('overview-card-info', {
				[`${customClasses}`]: !!customClasses,
			})}
		>
			<div className="item-container">
				<div className="item">
					{info}

					<div className="flex items-center justify-end gap-3 mt-4">
						{displayFormActionBtn && (
							<Button
								type="default"
								onClick={handleClick}
								icon={formActionType === 'Add' ? <AddOutlined /> : <EditOutlined />}
								className="flex items-center border-blue-100 bg-blue-50 hover:bg-blue-100 hover:border-blue-200 text-blue-700"
							>
								{formActionType}
							</Button>
						)}
						{displayTutorialActionBtn && !loggedInUser?.resellerClient && (
							<LearnMore tutorialId={tutorialId} debrand={debrand} />
						)}
					</div>
				</div>
			</div>
			<CustomModal
				width={width}
				title={
					<div className="flex items-center">
						{icon && <div className="text-blue-600 mr-2">{icon}</div>}
						<h1 className="text-xl font-semibold text-gray-800">{formTitle}</h1>
					</div>
				}
				children={form}
				confirmAction={handleConfirm}
				footer={footer}
				modalId={modalId}
				className="[&_.ant-modal-content]:p-0 
                  [&_.ant-modal-header]:p-0 
                  [&_.ant-modal-header]:m-0 
                  [&_.ant-modal-header]:border-b 
                  [&_.ant-modal-header]:border-gray-200 
                  [&_.ant-modal-header]:bg-gray-50
                  [&_.ant-modal-title]:px-6 
                  [&_.ant-modal-title]:py-4
                  [&_.ant-modal-body]:p-6
                  [&_.ant-modal-title]:flex 
                  [&_.ant-modal-title]:items-center
                  [&_.ant-btn-primary]:bg-blue-600 
                  [&_.ant-btn-primary]:border-blue-600 
                  [&_.ant-btn-primary:hover]:bg-blue-700 
                  [&_.ant-btn-primary:hover]:border-blue-700
                  [&_.ant-btn-default]:border-gray-200
                  [&_.ant-btn-default:hover]:border-blue-600
                  [&_.ant-btn-default:hover]:text-blue-600
                  responsive-modal"
			/>
		</div>
	);
};
